import type { AnyObject } from '../../types';
import { getCookie } from '../common/get-cookie';
import { isDefined, isUndefined } from '../common/is-type';
import domainName from '../common/root-domain-name';
import { getMarketingDataFromApi, getParamsFromCookie } from '../handler/marketing-tracking-handler';
import { getCookieObject, getMktTrackDataFromApi, getParamFromQs } from '../handler/mkttrack-cookie-handler';
import producttracking from '../handler/product-tracking-handler';
import { setData } from './lta-data';

const referrerUrl = document.referrer;
const referrerUrlHost = referrerUrl ? new URL(document.referrer).hostname : null;
const qsObj: AnyObject = Object.fromEntries(new URLSearchParams(location.search));
const mktTrackCookieName = 'MKT_TRACK';
const marketingCookieName = 'LT_MKT_TRACK';
const userOriginationDataCookieName = 'UserOriginationData';
let mktTrackCookieObj: any = null;
export let isTrackingReady = false;

export const hasNoValidSession = (): boolean => {
  const getMktTrackCookieByName = getCookie(mktTrackCookieName);
  if (getMktTrackCookieByName) {
    mktTrackCookieObj = JSON.parse(decodeURIComponent(getMktTrackCookieByName));
  }

  if (!mktTrackCookieObj) {
    return true;
  }

  if (referrerUrl && referrerUrlHost && !referrerUrlHost.includes(domainName())) {
    return true;
  }

  if (mktTrackCookieObj && qsObj.mtaid && qsObj.mtaid.toLowerCase() !== mktTrackCookieObj.mtaid.toLowerCase()) return true;

  if (mktTrackCookieObj?.timestamp) {
    const oneDay = 24 * 60 * 60 * 1000;
    const cookieTimestamp = new Date(mktTrackCookieObj.timestamp);
    const currentUtcTimestamp = new Date();

    return currentUtcTimestamp.valueOf() - cookieTimestamp.valueOf() > oneDay;
  }

  // DO NOT call API if above conditions are false
  return !isTrackingReady;
};

export const getMTAIDSource = (objType: any): Record<string, string> => {
  let MTAIDSource: string;
  const trackingObj: AnyObject = getCookieObject();
  if (objType === 'API') {
    if ((isDefined(trackingObj?.mtaid) && trackingObj.mtaid === getParamFromQs('mtaid')) || getParamFromQs('mtaid')) {
      MTAIDSource = 'URL';
    } else {
      MTAIDSource = 'API';
    }
  } else if (isUndefined(trackingObj?.mtaid)) {
    MTAIDSource = '';
  } else {
    MTAIDSource = 'Cookie';
  }

  return {
    MTAIDSource,
  };
};

export const emitTrackingReady = (responseData: string[]): void => {
  if (responseData && responseData.length > 0) {
    const dataObj: Record<string, string> = {};
    for (const currVal of responseData) {
      if (currVal.startsWith(mktTrackCookieName)) {
        Object.assign(dataObj, getMktTrackDataFromApi(currVal));
      } else if (currVal.startsWith(marketingCookieName)) {
        Object.assign(dataObj, getMarketingDataFromApi(currVal));
      } else if (currVal.startsWith(userOriginationDataCookieName)) {
        Object.assign(dataObj, getMktTrackDataFromApi(currVal, userOriginationDataCookieName));
      }
    }

    if (Object.keys(dataObj).length > 0) {
      Object.assign(dataObj, producttracking.get());
      setData(dataObj);
    }

    if (Object.keys(dataObj).length > 0) {
      const mtaidSource = dataObj.mtaid ? getMTAIDSource('API') : {};
      Object.assign(dataObj, mtaidSource);
      setData(dataObj);
    }
  } else {
    const trackProperties = {
      ...getParamsFromCookie(),
      ...getCookieObject(),
      ...producttracking.get(),
      ...getCookieObject(userOriginationDataCookieName),
      ...getMTAIDSource(null),
    };
    setData(trackProperties);
  }
  const event = new CustomEvent('TRACKING_READY');
  document.documentElement.dispatchEvent(event);
  isTrackingReady = true;
};

export default (): void => {
  if (hasNoValidSession()) {
    const crossDomainUrl: URL = new URL(`https://${SPLITTER_HOST}/api/general/details`);
    crossDomainUrl.searchParams.set('originalReferrer', document.referrer);
    crossDomainUrl.searchParams.set('originalURL', document.URL);
    fetch(crossDomainUrl, {
      credentials: 'include',
      keepalive: true,
    })
      .then((res) => res.json())
      .then(({ cookies }) => {
        const domain = location.hostname;
        const cookieDomain = domain.startsWith('www') ? domain.split('www.')[1] : domain;
        // Optionally log cookies before processing
        // Check if the domain is not 'lendingtree.com'
        if (!domain.endsWith('lendingtree.com')) {
          // Set cookies based on the domain
          for (const cookieStr of cookies) {
            // Extract the domain attribute from each cookie string if needed
            // Note: This is a simple approach. Adjust the parsing as needed based on your cookie format
            document.cookie = `${cookieStr};domain=.${cookieDomain}`;
            // Optionally log each cookie being set
          }
        }
        emitTrackingReady(cookies);
      })
      .catch((e) => {
        console.error(e);
        emitTrackingReady([]);
      });
  } else {
    emitTrackingReady([]);
  }
};
